/* eslint-disable max-lines */
import React, { RefObject, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from '@im/ui-lib/zenith/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Icon } from '@im/ui-lib/zenith/Icon';
import { InputSearch } from '@im/ui-lib/zenith/InputSearch';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import { ImThemeProviderZenith } from '@im/im-theme';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';
import { DialogFooter } from '@im/ui-lib/zenith/DialogFooter';
import { useBillingAddressByCNPJ } from '../../../hooks/use-billing-address-by-cnpj';
import { LoadingIndicator } from '@im/ui-lib/zenith/LoadingIndicator';
import {
  SelectionGroup,
  SelectionGroupProps,
} from '@im/ui-lib/zenith/SelectionGroup';

type Props = {
  defaultAddress: any;
  getFormattedAddress: any;
  headerSelectedAddress: any;
  isMyBusiness: boolean;
  onPrimaryClick: (myBusiness: boolean, selectedAddress: any) => void;
  selectedTab: 'business' | 'customer';
  setPopoverOpen: (value: boolean) => void;
  shipToAddresses: any;
};

export const BillingModeSelectorContent = (Props: Props) => {
  const {
    defaultAddress,
    getFormattedAddress,
    headerSelectedAddress,
    isMyBusiness,
    onPrimaryClick,
    selectedTab,
    setPopoverOpen,
    shipToAddresses,
  } = Props;
  const [inputValue, setInputValue] = useState('');
  const [enableAddressByCNPJ, setEnableAddressByCNPJ] = useState(false);

  const [inputErrorMessage, setInputErrorMessage] = useState<string>('');
  const {
    data: addressByCNPJ,
    error,
    isError,
    isLoading,
    isSuccess,
  } = useBillingAddressByCNPJ(
    inputValue,
    Boolean(inputValue) && !inputErrorMessage && enableAddressByCNPJ
  );
  const [customerAddressByCNPJ, setCustomerAddressByCNPJ] = useState<any>();
  const [selectionGroupData, setSelectionGroupData] = useState<
    SelectionGroupProps[]
  >([]);
  const [selectedRadioValue, setSelectedRadioValue] = useState(
    defaultAddress?.taxExemptNumber ?? ''
  );
  const [selectedAddress, setSelectedAddress] = useState<any>({});

  useEffect(() => {
    if (!isLoading && (isSuccess || isError)) {
      setCustomerAddressByCNPJ(isSuccess ? { ...addressByCNPJ?.[0] } : {});

      if (isError && error) {
        setInputErrorMessage(
          'This CNPJ doesn’t exist. Check for errors and try again.'
        );
      }
      setEnableAddressByCNPJ(false);
    }
  }, [isLoading, isError, addressByCNPJ, error, isSuccess]);

  useEffect(() => {
    if (!isMyBusiness) {
      const cusTab = selectedTab === 'customer';
      setCustomerAddressByCNPJ(cusTab ? headerSelectedAddress : {});
      setSelectedAddress(cusTab ? headerSelectedAddress : {});
    } else {
      setInputValue('');
      const tab = selectedTab !== 'business';
      setSelectedAddress(tab ? defaultAddress : headerSelectedAddress);
      setSelectedRadioValue(
        tab
          ? defaultAddress?.taxExemptNumber
          : headerSelectedAddress?.taxExemptNumber
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyBusiness]);

  const handleChangeRadio = (radioRef: RefObject<HTMLInputElement>) => {
    if (radioRef.current) {
      setSelectedRadioValue(radioRef.current.value);

      const address = shipToAddresses?.filter(
        (f: any) => f.taxExemptNumber === radioRef?.current?.value
      )[0];
      setSelectedAddress(address);
    }
  };

  const getSelectedStatus = (value: string) => {
    return selectedRadioValue === value;
  };

  useEffect(() => {
    setSelectionGroupData(
      shipToAddresses?.map((address: any) => ({
        headerText: 'CNPJ',
        subHeaderText: address?.taxExemptNumber,
        summary: getFormattedAddress(address),
        value: address?.taxExemptNumber,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipToAddresses]);

  useEffect(() => {
    if (customerAddressByCNPJ?.taxExemptNumber) {
      setSelectedAddress(customerAddressByCNPJ);
    }
  }, [customerAddressByCNPJ]);

  const validateInput = () => {
    const regex = /^[0-9]+$/;
    const maxLength = 14;
    const length = inputValue.length;

    if (inputValue) {
      if (!regex.test(inputValue)) {
        setInputErrorMessage('CNPJ should only include numbers.');
      } else if (length > maxLength || length < maxLength) {
        setInputErrorMessage('CNPJ should be exactly 14 digits.');
      } else {
        setInputErrorMessage('');
        setEnableAddressByCNPJ(true);
      }
    }
  };

  const singleBillingCard = (
    headerText: string,
    subHeaderText: string,
    summary: string,
    isCancelRequired: boolean
  ) => {
    return (
      <Item>
        <Stack direction={'row'} p={4} spacing={1}>
          <Stack direction={'column'}>
            <Stack direction={'row'} spacing={1}>
              <Typography color="primary.800" variant="text-sm-medium">
                {headerText}
              </Typography>
              <Typography color="primary.700" variant="text-sm-regular">
                {subHeaderText}
              </Typography>
            </Stack>
            <Typography color="primary.700" variant="text-sm-regular">
              {summary}
            </Typography>
          </Stack>
          {isCancelRequired && (
            <Button
              onClick={() => {
                setCustomerAddressByCNPJ({});
                setSelectedAddress({});
                setInputValue('');
              }}
              size="small"
              sx={{ p: '0px !important' }}
              variant="tertiary"
            >
              <Icon iconId="XClose" />
            </Button>
          )}
        </Stack>
      </Item>
    );
  };

  return (
    <ImThemeProviderZenith>
      <MuiProviders>
        <Stack>
          {isMyBusiness ? (
            <Stack
              px={4}
              py={2}
              spacing={2}
              sx={{
                maxHeight: '336px',
                //minHeight: '156px',
                overflowX: 'hidden',
                overflowY: 'scroll',
              }}
            >
              <Typography variant="text-sm-medium">
                {' '}
                {selectionGroupData?.length > 1
                  ? 'Your ship-tos'
                  : 'Your ship-to'}
              </Typography>
              {selectionGroupData?.length > 1
                ? selectionGroupData?.map((address: any, index: number) => (
                    <SelectionGroup
                      convertTxtToBlue={getSelectedStatus}
                      isSelected={getSelectedStatus(address.value)}
                      key={address.value}
                      onChangeRadio={handleChangeRadio}
                      selectionType="single-select"
                      size="sm"
                      type="Radio button"
                      {...address}
                    />
                  ))
                : selectionGroupData &&
                  singleBillingCard(
                    selectionGroupData?.[0]?.headerText ?? '',
                    selectionGroupData?.[0]?.subHeaderText ?? '',
                    selectionGroupData?.[0]?.summary ?? '',
                    false
                  )}
            </Stack>
          ) : (
            <Box sx={{ maxHeight: '336px', minHeight: '156px' }}>
              {customerAddressByCNPJ &&
              customerAddressByCNPJ?.taxExemptNumber ? (
                <Stack>
                  <Typography
                    color="gray.700"
                    sx={{
                      '&:after': {
                        color: '#D92D20',
                        content: '" *"',
                      },
                      pt: 2,
                      px: 4,
                    }}
                    variant="text-sm-medium"
                  >
                    Search ship-to CNPJ
                  </Typography>
                  <Box px={4} py={2}>
                    {singleBillingCard(
                      'CNPJ',
                      customerAddressByCNPJ?.taxExemptNumber ?? '',
                      getFormattedAddress(customerAddressByCNPJ),
                      true
                    )}
                  </Box>
                </Stack>
              ) : (
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  padding={'8px 16px'}
                  sx={{
                    alignItems: 'self-start',
                    gap: 2,
                    justifyContent: 'space-between',
                  }}
                >
                  <InputSearch
                    destructive={Boolean(inputErrorMessage)}
                    hintText={
                      inputErrorMessage
                        ? inputErrorMessage
                        : 'Enter the 14-digit CNPJ number (numbers only, no spaces or punctuation).'
                    }
                    inputValue={inputValue}
                    label="Search ship-to CNPJ"
                    onInputSearch={(value: string) => {
                      setInputValue(value);
                      setInputErrorMessage('');
                    }}
                    placeholder="XXXXXXXXXXXXXX"
                    required={true}
                    searchOptions={[]}
                    size="small"
                    width="282px"
                  />
                  <Stack pt={'28px'}>
                    {!enableAddressByCNPJ ? (
                      <Button
                        disabled={!inputValue || Boolean(inputErrorMessage)}
                        onClick={() => {
                          if (inputValue) {
                            validateInput();
                          }
                        }}
                        size="small"
                        variant="secondary-gray"
                      >
                        <Icon iconId="ArrowRight" />
                      </Button>
                    ) : (
                      <Box alignItems={'center'}>
                        <LoadingIndicator
                          color="light"
                          data-component="Template"
                          data-element="LoadingIndicator"
                          data-source-file="LoadingIndicator.stories.tsx"
                          loadingStyle="line-spinner"
                          spinnerSize={20}
                          supportingText=""
                          value={40}
                          variant="indeterminate"
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
          <DialogFooter
            divider
            onPrimaryButtonClick={() => {
              onPrimaryClick(
                isMyBusiness,
                isMyBusiness && shipToAddresses?.length <= 1
                  ? shipToAddresses?.[0]
                  : selectedAddress
              );
            }}
            onSecondaryButtonClick={() => {
              setPopoverOpen(false);
            }}
            primaryButtonProps={{
              disabled: !isMyBusiness && !selectedAddress?.taxExemptNumber,
            }}
            primaryButtonText="Confirm"
            secondaryButtonText="Cancel"
            type="horizontal-fill-container"
          />
        </Stack>
      </MuiProviders>
    </ImThemeProviderZenith>
  );
};

const Item = styled(Paper)(() => ({
  background: 'var(--color-primary-50, #EFF8FF)',
  border: '2px solid var(--color-primary-600, #1570EF)',
  borderRadius: '8px',
}));
